// รายชื่อนักเรียนรายห้อง (ภาคศาสนา) — หัวหน้าวิชาการศาสนา (pos 57)
//
// คู่ขนานกับ reports/StudentListByRoom.js ของภาคสามัญ และจงใจใช้ "รูปแบบการลิสต์รายชื่อ"
// ชุดเดียวกันทั้งหมด — คอลัมน์ เลขที่/เลขประจำตัว/ชื่อ/ชั้น/หอพัก/สถานะ/ปุ่มดูข้อมูล,
// สลับมุมมองตาราง–การ์ดสำหรับมือถือ, กดชื่อหอพักแล้วเห็นรายชื่อนักเรียนร่วมหอ —
// เพราะหัวหน้าวิชาการเปิดสองหน้านี้สลับกัน ถ้าคนละรูปแบบต้องมานั่งหาว่าคอลัมน์ไหนคืออะไรใหม่
//
// เลือกห้องทีละห้องจาก select เดียวแล้วค่อยยิง API เหมือนกัน — ไม่โหลดทั้งปีมารอไว้
// ห้องศาสนามีเป็นร้อยห้อง การดึงนักเรียนทั้งปี (สามพันกว่าแถวพร้อมชั้นสามัญกับหอพัก
// ของแต่ละคน) มากองไว้ก่อนทั้งที่ครูดูทีละห้อง เป็นงานที่เสียเปล่าทั้งฝั่ง DB และ
// ฝั่งเบราว์เซอร์ ยังไม่เลือกห้อง = ยังไม่มีคำถาม จึงยังไม่ต้องถาม server
//
// ต่างจากภาคสามัญอย่างเดียวคือคอลัมน์ชั้นเป็น "ชั้นสามัญ" (ภาคสามัญโชว์ชั้นศาสนาสลับกัน)
// เพราะเป็นข้อมูลอ้างอิงที่ใช้ข้ามไปคุยกับครูประจำชั้นอีกภาคหนึ่ง
//
// อ่านอย่างเดียว — การแก้ข้อมูลนักเรียนเป็นหน้าที่ของทะเบียนศาสนา (pos 52)
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  API, StudentAvatar, alertRequestError, genderLabel, roomLabel,
  useAcademicFilters, useDarkTables, useStudyYears,
} from './academicShared'

// st_status ของ tb_student (ชุดเดียวกันทั้งระบบ — server/utils/statusUtil.js)
//   '1' กำลังศึกษาอยู่ · '0' จำหน่ายแล้ว · '2' จบแล้ว · '3' นักเรียนใหม่
//
// '3' คือ **นักเรียนใหม่** ที่ทะเบียนศาสนาจัดเข้าห้องไว้แล้ว ไม่ใช่ผู้สมัครที่ยังไม่รับ —
// นับเป็นนักเรียนของห้องเต็มตัว ป้ายที่ติดไว้มีไว้ให้รู้ว่าใครเพิ่งเข้ามาปีนี้เท่านั้น
//
// ภาคสามัญแสดงสถานะเป็นข้อความสี ที่นี่ใช้ป้าย เพราะสีเหลืองของ "นักเรียนใหม่"
// คอนทราสต์ไม่พอถ้าเป็นสีตัวอักษรบนพื้นสว่าง (เหตุผลเดียวกับที่ academicShared เตือนไว้)
const STATUS = {
  '0': { tone: 'danger',  key: 'withdrawn' },
  '2': { tone: 'success', key: 'graduated' },
  '3': { tone: 'warning', key: 'newcomer' },
}
const statusOf = (s) => STATUS[s] || { tone: 'primary', key: 'active' }

// เพศของห้อง: น้ำเงิน = ชาย, แดง = หญิง — ชุดสีเดียวกับที่ใช้อยู่ทั่วเมนูภาคศาสนา
// (IslamicAdvisorHome, IslamicAdvisorStudents, IslamicClassGrade ฯลฯ)
//
// สีเป็นตัวช่วยอ่านเร็วเท่านั้น ไม่ใช่ตัวบอกความหมายตัวเดียว — ป้ายชื่อห้องใน select
// เขียนคำว่า ชาย/หญิง กำกับไว้ด้วยเสมอ เพราะสีของ <option> เป็นสิ่งที่เบราว์เซอร์
// เลือกจะไม่ทำตามได้ (Safari ไม่ลงสีให้) และคนตาบอดสีก็ต้องเลือกห้องถูกเหมือนกัน
const GENDER_TONE = { m: 'primary', f: 'danger' }
const genderColor = (g) => (GENDER_TONE[g] ? `var(--bs-${GENDER_TONE[g]})` : undefined)

const StatusBadge = ({ t, status }) => {
  const { tone, key } = statusOf(status)
  return (
    <Badge bg={tone} text={tone === 'warning' ? 'dark' : undefined} className="fs-xs">
      {t.academic.studentList.status[key]}
    </Badge>
  )
}

// ป้ายห้องปลายทางของคนที่ย้ายห้อง — ประกอบแบบเดียวกับ roomLabel ของห้องต้นทาง
// (ชั้น/เลขห้อง + รหัสโปรแกรมถ้าไม่ใช่ห้องปกติ) เพราะชั้นเดียวกันมีเลขห้องซ้ำกันได้
// ระหว่างห้องปกติกับ AIP/STP
const transferLabel = (t, s) => {
  if (!s.transfer_class && !s.transfer_room_no) return t.academic.studentList.movedToUnknown
  const prog = s.transfer_program && s.transfer_program !== 'GP' ? ` (${s.transfer_program})` : ''
  return t.academic.studentList.movedTo(`${s.transfer_class ?? ''}/${s.transfer_room_no ?? ''}${prog}`)
}

// สีพื้นแถว/การ์ดบอกสองเรื่องที่ต้องสะดุดตาโดยไม่ต้องอ่าน: ออกแล้ว (แดง) / ย้ายห้อง (เหลือง)
const rowTone = (s) => (s.st_status === '0' ? 'danger' : s.transfer_room_id ? 'warning' : null)

const DormLink = ({ t, s, onClick }) => (
  s.dorm_name
    ? (
      <span
        className="text-primary"
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        title={t.academic.studentList.dormTip}
        onClick={() => onClick(s)}
      >
        {s.dorm_name}
      </span>
    )
    : <span className="text-secondary">-</span>
)

// หน้าเจาะรายคนของภาคศาสนา — pos 57 เข้าได้อยู่แล้ว (islamic/student-tracking/:id)
// ส่ง yearId ไปด้วยเสมอ ไม่ปล่อยให้หน้าปลายทางตกกลับไปอ่าน localStorage เอง
// ไม่งั้นกดจากรายชื่อปี 2568 แล้วไปโผล่ข้อมูลปีที่เลือกไว้ล่าสุดของหน้าอื่น
const studentLink = (s, yearId) =>
  `/islamic/student-tracking/${s.st_id}${yearId ? `?yearId=${yearId}` : ''}`

// การ์ดนักเรียน 1 คน สำหรับมุมมองมือถือ — ข้อมูลชุดเดียวกับหนึ่งแถวของตาราง
const StudentCard = ({ t, lang, s, index, yearId, onDormClick }) => {
  const tone = rowTone(s)
  return (
    <div className={`border rounded p-3 h-100 ${tone ? `bg-${tone}-subtle` : ''}`}>
      <div className="d-flex align-items-center gap-2">
        <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={32} />
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate"><StudentName student={s} lang={lang} /></div>
          <div className="fs-xs text-secondary">#{index + 1} · {s.st_idnumber || '-'}</div>
          {s.transfer_room_id && (
            <div className="fs-xs text-secondary">{transferLabel(t, s)}</div>
          )}
        </div>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div>
          <span className="text-secondary">{t.academic.studentList.colRegularClass}:</span>{' '}
          {s.regular_class || <span className="text-secondary">-</span>}
        </div>
        <div>
          <span className="text-secondary">{t.academic.studentList.colDorm}:</span>{' '}
          <DormLink t={t} s={s} onClick={onDormClick} />
        </div>
        <div className="mt-1"><StatusBadge t={t} status={s.st_status} /></div>
      </div>
      <div className="mt-2 pt-2 border-top">
        <Link
          to={studentLink(s, yearId)}
          className="btn btn-sm btn-outline-primary w-100"
          title={t.academic.studentList.viewBtnTip}
        >
          <i className="ri-eye-line me-1" />{t.academic.studentList.viewBtn}
        </Link>
      </div>
    </div>
  )
}

export default function IslamicStudentListByRoom() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  const filters = useAcademicFilters(yearId, t)
  const [rows, setRows] = useState([])
  // มือถือเริ่มที่มุมมองการ์ด ตารางเจ็ดคอลัมน์อ่านบนจอแคบไม่ไหว (เหมือนภาคสามัญ)
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const [dormModal, setDormModal] = useState(null)
  const [dormResidents, setDormResidents] = useState([])
  const [dormLoaded, setDormLoaded] = useState(false)

  // ห้องใน select จัดกลุ่มตามชั้น โดยคงลำดับที่ server ส่งมา (compareClass เรียง
  // "2" ก่อน "10" แล้ว) — จัดกลุ่มด้วยการไล่แถวต่อกัน ไม่ใช่ sort ใหม่ฝั่ง client
  const roomGroups = useMemo(() => {
    const out = []
    for (const r of filters.rooms) {
      const last = out[out.length - 1]
      if (last && last.ic_id === r.ic_id) last.rooms.push(r)
      else out.push({ ic_id: r.ic_id, ic_class: r.ic_class, rooms: [r] })
    }
    return out
  }, [filters.rooms])

  const selectedRoom = useMemo(
    () => filters.rooms.find(r => String(r.ir_id) === String(filters.roomId)) || null,
    [filters.rooms, filters.roomId])

  // จำห้องที่เลือกไว้ เพื่อให้กด "ดูข้อมูล" แล้วย้อนกลับมายังอยู่ห้องเดิม
  // (ภาคสามัญทำแบบเดียวกันด้วยคีย์ slbr_room) เก็บใน sessionStorage ไม่ใช่ localStorage
  // เพราะเป็นบริบทของการนั่งดูรอบนี้ ไม่ใช่ค่าตั้งประจำตัวแบบปีการศึกษา
  const setRoomId = filters.setRoomId
  useEffect(() => {
    const saved = sessionStorage.getItem('islbr_room')
    if (!saved || filters.roomId) return
    if (filters.rooms.some(r => String(r.ir_id) === saved)) setRoomId(saved)
  }, [filters.rooms, filters.roomId, setRoomId])
  useEffect(() => {
    sessionStorage.setItem('islbr_room', filters.roomId || '')
  }, [filters.roomId])

  // ยิง API ต่อเมื่อเลือกห้องแล้วเท่านั้น — ยังไม่เลือก = ล้างรายชื่อเดิมทิ้ง
  // ไม่ปล่อยให้รายชื่อห้องก่อนหน้าค้างอยู่บนจอทั้งที่ช่องเลือกว่างแล้ว
  const roomId = filters.roomId
  useEffect(() => {
    if (!yearId || !roomId) { setRows([]); return }
    let alive = true
    setLoading(true)
    axiosInstance.get(`${API}/islamic/academic/studentList/${yearId}?roomId=${roomId}`)
      .then(res => { if (alive) setRows(Array.isArray(res.data) ? res.data : []) })
      .catch(err => { if (alive) { setRows([]); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [yearId, roomId, setLoading, t])

  // รายชื่อนักเรียนร่วมหอ — ใช้ endpoint เดียวกับภาคสามัญ (requireLogin อย่างเดียว
  // ไม่ผูกกับตำแหน่ง) ข้อมูลหอพักไม่ได้แยกภาคอยู่แล้ว
  const openDormModal = (s) => {
    setDormModal({
      dorm_id: s.dorm_id, dorm_name: s.dorm_name, dorm_code: s.dorm_code,
      village_name: s.village_name, st_id: s.st_id,
    })
    setDormResidents([])
    setDormLoaded(false)
    axiosInstance.get(`${API}/getDormResidents/${s.dorm_id}`)
      .then(res => setDormResidents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDormResidents([]))
      .finally(() => setDormLoaded(true))
  }

  // ไม่กรองฝั่ง client เลย — รายชื่อที่เห็นคือทั้งห้องตามที่ server ส่งมา
  // คนที่ออกแล้ว ('0') และคนที่ย้ายห้องออกไปก็อยู่ในรายชื่อพร้อมป้ายบอกสถานะ/ห้องปลายทาง
  // เพราะหัวหน้าวิชาการเปิดหน้านี้เพื่อเทียบกับสมุดรายชื่อของครูประจำชั้น ซึ่งมีชื่อคนเหล่านี้อยู่
  //
  // จัดกลุ่มเป็นห้อง — เรียงตามที่ server ส่งมา (ชั้นเรียงตามเลขจริง ไม่ใช่ตามข้อความ)
  // เลือกได้ทีละห้องอยู่แล้ว จึงเหลือกลุ่มเดียวเสมอ แต่คงการจัดกลุ่มไว้เพราะยอดหัวห้อง
  // ต้องนับจากแถวที่ได้มาจริง ไม่ใช่เชื่อว่าทุกแถวเป็นห้องเดียวกัน
  const groups = useMemo(() => {
    const out = []
    for (const r of rows) {
      const last = out[out.length - 1]
      if (last && last.ir_id === r.ir_id) last.students.push(r)
      else out.push({ ir_id: r.ir_id, head: r, students: [r] })
    }
    // ยอดหลักของห้อง = คนที่ยังอยู่จริง ส่วนคนที่ออก/ย้ายออกแยกเป็นป้ายของตัวเอง
    // ไม่งั้นเลขหัวห้องจะเฟ้อขึ้นทุกปีตามจำนวนคนที่เคยผ่านห้องนี้
    for (const g of out) {
      g.newcomers = g.students.filter(s => s.st_status === '3').length
      g.left      = g.students.filter(s => s.st_status === '0').length
      g.moved     = g.students.filter(s => s.transfer_room_id).length
      g.inRoom    = g.students.filter(s => s.st_status !== '0' && !s.transfer_room_id).length
    }
    return out
  }, [rows])

  return (
    <React.Fragment>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">{t.academic.groupTitle}</li>
            <li className="breadcrumb-item active">{t.academic.studentList.pageTitle}</li>
          </ol>
          <h4 className="main-title mb-0">{t.academic.studentList.pageTitle}</h4>
        </div>
        <div className="fs-sm text-secondary">
          {filters.roomId ? t.common.studentCount(rows.length) : ''}
        </div>
      </div>

      <Card className="card-one mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.academic.filterYear}</Form.Label>
              <Form.Select size="sm" value={yearId} onChange={e => setYearId(e.target.value)}>
                {years.map(y => (
                  <option key={y.year_id} value={y.year_id}>{t.common.studyYearShort(y.year_year)}</option>
                ))}
              </Form.Select>
            </Col>
            {/* ห้องทั้งหมดอยู่ใน select เดียว จัดกลุ่มตามชั้นด้วย optgroup เพราะชั้นศาสนา
                มีตั้งแต่ 2–10 บวกห้องพิเศษ AIP/STP รวมกันเป็นร้อยห้อง รายการเรียงเฉย ๆ
                หาห้องไม่เจอ (ลำดับชั้นมาจาก server ซึ่งเรียงด้วย compareClass แล้ว
                "10" จึงอยู่หลัง "2" ไม่ใช่ก่อน) */}
            <Col xs={12} md={5} lg={4}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.academic.filterRoom}</Form.Label>
              {/* ลงสีที่ตัว select ด้วย ไม่ใช่ที่ <option> อย่างเดียว — ช่องที่ปิดอยู่
                  แสดงห้องที่เลือกด้วยสีของตัว select เอง เบราว์เซอร์ที่ไม่ลงสีให้
                  <option> จึงยังเห็นเพศของห้องที่กำลังเปิดดูอยู่ */}
              <Form.Select
                size="sm"
                value={filters.roomId}
                onChange={e => filters.setRoomId(e.target.value)}
                style={{ color: genderColor(selectedRoom?.ir_gender) }}
              >
                <option value="" style={{ color: 'var(--bs-body-color)' }}>
                  {t.academic.studentList.selectRoom}
                </option>
                {roomGroups.map(grp => (
                  <optgroup key={grp.ic_id} label={t.academic.studentList.roomHeading(grp.ic_class)}>
                    {grp.rooms.map(r => (
                      <option key={r.ir_id} value={r.ir_id} style={{ color: genderColor(r.ir_gender) }}>
                        {`${roomLabel(r)} · ${genderLabel(t, r.ir_gender)}`}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')}
                  title={t.academic.studentList.viewTable}
                >
                  <i className="ri-table-line" />
                </button>
                <button
                  type="button"
                  className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')}
                  title={t.academic.studentList.viewCard}
                >
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ยังไม่เลือกห้องกับเลือกแล้วไม่มีใครเลย เป็นคนละเรื่องกัน — ข้อความเดียวกัน
          จะทำให้ครูนึกว่าห้องนั้นว่างทั้งที่ยังไม่ได้เลือกห้องด้วยซ้ำ */}
      {groups.length === 0 && (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">
            <i className={`fs-3 d-block mb-2 ${filters.roomId ? 'ri-team-line' : 'ri-list-check-2'}`} />
            {filters.roomId ? t.academic.studentList.noStudents : t.academic.studentList.pickRoomFirst}
          </Card.Body>
        </Card>
      )}

      {groups.map(g => (
        <Card className="card-one mb-3" key={g.ir_id}>
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">
              {t.academic.studentList.roomHeading(roomLabel(g.head))}
            </Card.Title>
            <Badge bg={GENDER_TONE[g.head.ir_gender] || 'secondary'} className="fs-xs">
              {genderLabel(t, g.head.ir_gender)}
            </Badge>
            <Badge bg="primary" className="fs-xs">{t.academic.studentList.inRoomCount(g.inRoom)}</Badge>
            {/* คนที่ออก/ย้ายออกอยู่ในตารางด้วย ป้ายพวกนี้จึงต้องบอกว่ายอดที่เห็นในตาราง
                มากกว่ายอด "อยู่ในห้อง" เพราะอะไร */}
            {g.newcomers > 0 && (
              <Badge bg="warning" text="dark" className="fs-xs">
                {t.academic.studentList.newcomerCount(g.newcomers)}
              </Badge>
            )}
            {g.left > 0 && (
              <Badge bg="danger" className="fs-xs">
                {t.academic.studentList.leftCount(g.left)}
              </Badge>
            )}
            {g.moved > 0 && (
              <Badge bg="secondary" className="fs-xs">
                {t.academic.studentList.movedCount(g.moved)}
              </Badge>
            )}
            {g.head.advisor_names && (
              <span className="fs-xs text-secondary">
                {t.academic.studentList.advisor}: {g.head.advisor_names}
              </span>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            {view === 'card' ? (
              <div className="p-3">
                <Row className="g-3">
                  {g.students.map((s, i) => (
                    <Col key={s.is_study_id} xs={12} md={6} xl={4}>
                      <StudentCard t={t} lang={lang} s={s} index={i} yearId={yearId} onDormClick={openDormModal} />
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <Table hover responsive className="mb-0 align-middle">
                <thead className="table-secondary">
                  <tr>
                    <th scope="col" className="text-center" style={{ width: 60 }}>{t.academic.studentList.colNo}</th>
                    <th scope="col" className="text-center" style={{ width: 110 }}>{t.academic.studentList.colId}</th>
                    <th scope="col">{t.academic.studentList.colName}</th>
                    <th scope="col" className="text-center" style={{ width: 110 }}>{t.academic.studentList.colRegularClass}</th>
                    <th scope="col" className="text-center" style={{ width: 160 }}>{t.academic.studentList.colDorm}</th>
                    <th scope="col" className="text-center" style={{ width: 140 }}>{t.academic.studentList.colStatus}</th>
                    <th scope="col" className="text-center" style={{ width: 110 }}>{t.academic.studentList.colInfo}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.students.map((s, i) => (
                    <tr key={s.is_study_id} className={rowTone(s) ? `table-${rowTone(s)}` : undefined}>
                      <th scope="row" className="text-center">{i + 1}</th>
                      <td className="text-center">{s.st_idnumber || '-'}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div className="text-truncate"><StudentName student={s} lang={lang} /></div>
                            {s.transfer_room_id && (
                              <small className="text-secondary">{transferLabel(t, s)}</small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        {s.regular_class || <span className="text-secondary">-</span>}
                      </td>
                      <td className="text-center"><DormLink t={t} s={s} onClick={openDormModal} /></td>
                      <td className="text-center"><StatusBadge t={t} status={s.st_status} /></td>
                      <td className="text-center">
                        <Link
                          to={studentLink(s, yearId)}
                          className="btn btn-sm btn-outline-primary"
                          title={t.academic.studentList.viewBtnTip}
                        >
                          <i className="ri-eye-line me-1" />{t.academic.studentList.viewBtn}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      ))}

      <Modal show={!!dormModal} onHide={() => setDormModal(null)} centered scrollable size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h6">
            {t.academic.studentList.dormTitle(dormModal?.dorm_name ?? '')}
            {dormModal?.dorm_code && <span className="text-secondary ms-1">({dormModal.dorm_code})</span>}
            {dormModal?.village_name && (
              <span className="text-secondary ms-2">{t.academic.studentList.dormVillage(dormModal.village_name)}</span>
            )}
            <Badge bg="secondary" className="ms-2">{t.common.personCount(dormResidents.length)}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {!dormLoaded ? (
            <div className="text-center py-5 text-secondary">{t.academic.studentList.loading}</div>
          ) : dormResidents.length === 0 ? (
            <div className="text-center py-5 text-secondary">{t.academic.studentList.dormEmpty}</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-secondary">
                <tr>
                  <th scope="col" className="text-center" style={{ width: 60 }}>{t.academic.studentList.colNo}</th>
                  <th scope="col" className="text-center" style={{ width: 110 }}>{t.academic.studentList.colId}</th>
                  <th scope="col">{t.academic.studentList.colName}</th>
                  <th scope="col" className="text-center" style={{ width: 110 }}>{t.academic.studentList.dormColClass}</th>
                  <th scope="col" className="text-center" style={{ width: 110 }}>{t.academic.studentList.dormColIslamic}</th>
                </tr>
              </thead>
              <tbody>
                {dormResidents.map((r, i) => (
                  // ไฮไลต์คนที่กดเข้ามา เพื่อให้เห็นว่าตัวเองอยู่ตรงไหนในรายชื่อหอ
                  <tr key={r.st_id} className={r.st_id === dormModal?.st_id ? 'table-primary' : undefined}>
                    <th scope="row" className="text-center">{i + 1}</th>
                    <td className="text-center">{r.st_idnumber || '-'}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <StudentAvatar photo={r.st_photo} name={studentDisplayName(r, lang)} size={28} />
                        <span className="text-truncate"><StudentName student={r} lang={lang} /></span>
                      </div>
                    </td>
                    <td className="text-center">
                      {r.c_no && r.r_no ? `ม.${r.c_no}/${r.r_no}` : <span className="text-secondary">-</span>}
                    </td>
                    <td className="text-center">
                      {r.ic_class ? `${r.ic_class}/${r.isl_room_no}` : <span className="text-secondary">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  )
}
